/**
 * Unread Help Chat replies.
 * A reply is one completed bot message. The first snapshot is history and does
 * not count. Later replies count once, until the chat is open and visible.
 * The stored text must match the failure message written by the help chat agent.
 */

const BADGE_CAP = 9;
const SEEN_LIMIT = 2000;
const VIEWING_TTL_MS = 4000;
const ERROR_REPLY = "Sorry, I encountered an error while processing your message. Please try again.";

function storageKey(userId) {
  return `stc.helpChat.unread.${userId}`;
}

function viewingKey(userId) {
  return `stc.helpChat.viewing.${userId}`;
}

function emptyState() {
  return { primed: false, seen: [], unread: [] };
}

function latestUnread(messages, unreadKeys) {
  const unread = new Set(unreadKeys);
  let latest = null;
  for (const item of messages) {
    if (!unread.has(item.key)) continue;
    if (!latest || String(item.message?.Time || "") > String(latest.message?.Time || "")) latest = item;
  }
  return latest;
}

function badgeLabel(count, cap = BADGE_CAP) {
  const total = Math.floor(Number(count));
  if (!Number.isFinite(total) || total <= 0) return "";
  if (total > cap) return `${cap}+`;
  return String(total);
}

function isErrorReply(message) {
  const text = typeof message === "string" ? message : message?.Message;
  return typeof text === "string" && text.trim() === ERROR_REPLY;
}

function completedReplies(list) {
  if (!list || typeof list !== "object") return [];
  const replies = [];
  for (const [conversationId, conversation] of Object.entries(list)) {
    const messages = conversation?.Messages;
    if (!Array.isArray(messages)) continue;
    messages.forEach((message, index) => {
      if (!message || message.Role !== "bot" || message.Pending) return;
      const time = typeof message.Time === "string" ? message.Time : "";
      replies.push({
        key: `${conversationId}:${time}:${index}`,
        messageId: `${conversationId}-${index}`,
        conversationId,
        message,
      });
    });
  }
  return replies;
}

function normaliseState(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  return {
    primed: source.primed === true,
    seen: Array.isArray(source.seen) ? source.seen.filter((key) => typeof key === "string") : [],
    unread: Array.isArray(source.unread) ? source.unread.filter((key) => typeof key === "string") : [],
  };
}

function trimSeen(seen, liveKeys) {
  const keys = [...seen];
  const capped = keys.length > SEEN_LIMIT ? keys.slice(keys.length - SEEN_LIMIT) : keys;
  const next = new Set(capped);
  for (const key of liveKeys) next.add(key);
  return [...next];
}

/**
 * Fold completed replies into the unread record.
 * Returns the replies that should produce one toast each.
 */
function absorbReplies(state, messages, visible) {
  const current = normaliseState(state);
  const seen = new Set(current.seen);
  const unread = new Set(current.unread);
  const live = messages.map((item) => item.key);

  if (!current.primed) {
    for (const key of live) seen.add(key);
    return {
      state: {
        primed: true,
        seen: trimSeen(seen, live),
        unread: visible ? [] : [...unread],
      },
      arrivals: [],
    };
  }

  const arrivals = [];
  for (const item of messages) {
    if (seen.has(item.key)) continue;
    seen.add(item.key);
    if (visible || unread.has(item.key)) continue;
    unread.add(item.key);
    arrivals.push(item);
  }

  if (visible) unread.clear();
  else {
    const liveKeys = new Set(live);
    for (const key of unread) {
      if (!liveKeys.has(key)) unread.delete(key);
    }
  }

  return {
    state: { primed: true, seen: trimSeen(seen, live), unread: [...unread] },
    arrivals,
  };
}

function loadUnreadState(storage, userId) {
  if (!storage || !userId) return emptyState();
  try {
    const raw = storage.getItem(storageKey(userId));
    if (!raw) return emptyState();
    return normaliseState(JSON.parse(raw));
  } catch {
    return emptyState();
  }
}

function saveUnreadState(storage, userId, state) {
  if (!storage || !userId) return false;
  try {
    const next = JSON.stringify(normaliseState(state));
    if (storage.getItem(storageKey(userId)) === next) return false;
    storage.setItem(storageKey(userId), next);
    return true;
  } catch {
    return false;
  }
}

function parseViewing(raw, now, ttlMs = VIEWING_TTL_MS) {
  if (typeof raw !== "string" || !raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const at = Number(parsed?.at);
    if (!parsed || typeof parsed.id !== "string" || !parsed.id) return null;
    if (!Number.isFinite(at) || now - at < 0 || now - at >= ttlMs) return null;
    return { id: parsed.id, at };
  } catch {
    return null;
  }
}

export {
  BADGE_CAP,
  ERROR_REPLY,
  SEEN_LIMIT,
  VIEWING_TTL_MS,
  absorbReplies,
  badgeLabel,
  completedReplies,
  isErrorReply,
  latestUnread,
  loadUnreadState,
  parseViewing,
  saveUnreadState,
  storageKey,
  viewingKey,
};
